// components/SelectBuyPopUp.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../slices/cartSlice';
import { trackAddToCart } from '../utils/gaAnalytics';
import { Form } from 'react-bootstrap';

import {
  RENTAL_RATE_NON_ORG,
  RENTAL_RATE_ORG,
  SHIPPING_FEE,
} from '../constants/constants';

// import styles from './SelectBuyPopUp.module.css';

const SelectBuyPopUp = ({ product }) => {
  const [isPopUpVisible, setPopUpVisible] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─────────────────── 状態 ─────────────────── */
  const [serviceOption, setServiceOption] = useState('rent');
  const [inStore, setInStore] = useState(true);
  const [useSubscription, setUseSubscription] = useState(false);
  const [rentalPeriod, setRentalPeriod] = useState('default');

  /* ─────────────────── 認証情報 ─────────────────── */
  const { userInfo } = useSelector((state) => state.auth);
  const canUseSubscription =
    !!userInfo?.subscription &&
    userInfo.subscription.usedItems < userInfo.subscription.maxItems &&
    (Number(product?.price) || 0) <= (userInfo.subscription.itemPriceLimit ?? Infinity);

  /* ─────────────────── 料金計算 ─────────────────── */
  const calcBasePrice = ({
    price = 0,
    serviceOption,
    useSubscription,
    canUseSubscription,
    isOrgUser,
    rentalPeriod,
  }) => {
    if (serviceOption !== 'rent') return price; // 購入
    if (useSubscription && canUseSubscription) return 0; // サブスク
    const table = isOrgUser ? RENTAL_RATE_ORG : RENTAL_RATE_NON_ORG;
    const rate = table[rentalPeriod] ?? table.default ?? 1;
    return Math.round(price * rate);
  };

  const basePrice = useMemo(
    () =>
      calcBasePrice({
        price: Number(product?.price) || 0,
        serviceOption,
        useSubscription,
        canUseSubscription,
        isOrgUser: !!userInfo?.organization,
        rentalPeriod,
      }),
    [
      product?.price,
      serviceOption,
      useSubscription,
      canUseSubscription,
      userInfo?.organization,
      rentalPeriod,
    ],
  );

  const shippingCost = useMemo(() => (inStore ? 0 : SHIPPING_FEE), [inStore]);
  const finalCost = useMemo(() => basePrice + shippingCost, [basePrice, shippingCost]);

  /* ─────────────────── カート投入 ─────────────────── */
  const addToCartHandler = () => {
    if (!product) return;
    const itemPayload = {
      ...product,
      serviceOption,
      inStore,
      useSubscription: canUseSubscription && useSubscription,
      rentalPeriod: !useSubscription ? rentalPeriod : 'unlimited',
      finalPrice: finalCost, // 送料込み
      shippingCost, // 内訳保持
    };
    dispatch(addToCart(itemPayload));
    trackAddToCart(itemPayload);
    navigate('/cart');
  };

  /* ─────────────────── 既存ロジック ─────────────────── */
  useEffect(() => {
    if (product && product.status !== 'available') {
      setServiceOption('buy');
    }
  }, [product]);

  /* ─────────────────── モーダル表示中のUX（スクロールロック・Esc閉じ） ─────────────────── */
  useEffect(() => {
    if (!isPopUpVisible) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') setPopUpVisible(false);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isPopUpVisible]);

  const openPopUp = () => {
    setServiceOption('buy');
    setPopUpVisible(true);
  };

  const closePopUp = () => setPopUpVisible(false);

  return (
    <div className="select-buy-popup">
      <button onClick={openPopUp} className="select-buy-popup-button">
        購入する
      </button>

      {isPopUpVisible && serviceOption === 'buy' && (
        <>
          {/* 背景オーバーレイ（クリックで閉じる） */}
          <div
            className="select-buy-popup-overlay"
            onClick={closePopUp}
            aria-hidden="true"
          />

          {/* ポップアップ本体（中央固定） */}
          <div
            className="select-buy-popup-modal"
            role="dialog"
            aria-modal="true"
            aria-label="購入オプション"
            onClick={(e) => e.stopPropagation()} // 中身クリックで閉じない
          >
            <p className="select-buy-popup-modal-title">受取方法をお選びください</p>
            <div className="select-buy-popup-modal-reserveoption">
              <Form.Check
                type="checkbox"
                label={`店舗で受け取る（${product?.shopArea?.name ?? '店舗'}）`}
                checked={inStore}
                onChange={(e) => setInStore(e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label={`郵送で受け取る（送料は880円）`}
                checked={!inStore}
                onChange={(e) => setInStore(!e.target.checked)}
              />

            </div>
            
            {/* 料金表示（送料込み） */}
            <div className="select-buy-popup-modal-price">
              <p className="select-buy-popup-modal-price-lable">小計：</p>
              <p className="select-buy-popup-modal-price-cost">
                ¥{finalCost.toLocaleString()}
              </p>
            </div>

            <div className="select-buy-popup-modal-actions">
              <button
                className="select-buy-popup-modal-actions-back"
                onClick={closePopUp}
              >
                商品ページに戻る
              </button>
              <button
                className="select-buy-popup-modal-actions-cart"
                onClick={addToCartHandler}
              >
                カートに入れる
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectBuyPopUp;
